import {Phone, Calendar, Shield, Award, Check, MapPin, Clock, MessageCircleDashed as MessageCircle, Car} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'
import CanonicalTag from '@/components/CanonicalTag'

const faqs = [
  {
    question: "How much does PPF cost for a BMW in San Diego?",
    answer: "BMW PPF pricing at Mr Wrap starts at $499 for front bumper coverage, $1,299 for partial front, $1,899 for full front, $4,499 for full vehicle gloss, $5,499 for matte or stealth finishes, and $5,999 for colored PPF. These are starting prices. Exact BMW pricing depends on the model, coverage, film, body complexity, paint prep, and installation requirements. An X5 or 7 Series often needs more film than a 3 Series. We confirm the number after looking at your BMW."
  },
  {
    question: "Is full-front or full-body PPF better for a new BMW?",
    answer: "Full-front PPF is the most common choice for a new BMW because the bumper, hood, fenders, and mirrors take the most highway debris on I-5, I-15, and Highway 78. Full-vehicle gloss or matte coverage makes more sense if you want every painted panel protected, you drive a lot of freeway miles, or you plan to keep the car long term. We help you choose based on how you drive, not a one-size package."
  },
  {
    question: "Can you PPF a leased BMW?",
    answer: "Yes. Paint protection film is commonly installed on leased BMWs because it helps keep factory paint cleaner during the lease and can be professionally removed later. PPF does not replace lease-return inspection rules, but it does reduce rock-chip and swirl-mark risk on high-impact panels."
  },
  {
    question: "Can PPF and window tint be installed during the same appointment?",
    answer: "Often yes. Many BMW owners combine paint protection film with premium ceramic window tint in one visit at our Vista shop. Whether both can be completed in the same appointment depends on the coverage selected, the BMW model, and the shop schedule. Tell us what you want on the quote form and we will plan the visit around the work."
  },
  {
    question: "How long does BMW PPF installation take?",
    answer: "Front bumper or partial-front work is typically about one day. Full-front BMW PPF usually takes about two days. Full-vehicle coverage generally takes three to five days depending on the model, film, and how much trim we need to remove. We do not rush curing or edge work to finish early."
  },
  {
    question: "Does PPF change the appearance of BMW paint?",
    answer: "Clear PPF is designed to stay optically clear over BMW paint, including common factory colors. When it is installed carefully, the film should not look like a wrap. Matte, stealth, and colored PPF are different: those films intentionally change the finish or color while still protecting the paint underneath."
  },
  {
    question: "Can matte BMW paint be protected?",
    answer: "Yes. Matte and Frozen BMW finishes need a matching matte or stealth film. Gloss PPF on matte paint would change the factory look. If your BMW has a matte, satin, or Frozen finish, tell us before we quote so we specify the right film."
  }
]

const BmwPpfSanDiegoPage = () => {
  const navigate = useNavigate()

  const goToQuote = () => {
    navigate("/contact")
    setTimeout(() => window.scrollTo(0, 0), 100)
  }

  useEffect(() => {
    const faqScript = document.createElement('script')
    faqScript.id = 'bmw-ppf-faq-schema'
    faqScript.type = 'application/ld+json'
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    })
    document.head.appendChild(faqScript)

    const serviceScript = document.createElement('script')
    serviceScript.id = 'bmw-ppf-service-schema'
    serviceScript.type = 'application/ld+json'
    serviceScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "BMW Paint Protection Film San Diego",
      "serviceType": "BMW Paint Protection Film (PPF)",
      "description": "BMW paint protection film installation in Vista, serving North County and San Diego. Clear, matte, stealth, and colored PPF with professional preparation and mixed installation methods selected by vehicle and panel.",
      "url": "https://autowrapsandiego.com/bmw-ppf-san-diego",
      "areaServed": ["San Diego", "Vista", "North County San Diego", "San Diego County"],
      "provider": {
        "@type": "LocalBusiness",
        "name": "Mr. Wrap",
        "telephone": "+1-858-282-8119",
        "email": "mrwrapmatt@gmail.com",
        "url": "https://autowrapsandiego.com/",
        "image": "https://static.lumi.new/71/7132523da35951c352e5739adcc9dffa.webp",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2244 South Santa Fe Avenue, Suite A7",
          "addressLocality": "Vista",
          "addressRegion": "CA",
          "postalCode": "92084",
          "addressCountry": "US"
        }
      }
    })
    document.head.appendChild(serviceScript)

    const breadcrumbScript = document.createElement('script')
    breadcrumbScript.id = 'bmw-ppf-breadcrumb-schema'
    breadcrumbScript.type = 'application/ld+json'
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://autowrapsandiego.com/" },
        { "@type": "ListItem", "position": 2, "name": "Paint Protection Film San Diego", "item": "https://autowrapsandiego.com/paint-protection-film-san-diego" },
        { "@type": "ListItem", "position": 3, "name": "BMW PPF San Diego", "item": "https://autowrapsandiego.com/bmw-ppf-san-diego" }
      ]
    })
    document.head.appendChild(breadcrumbScript)

    document.title = "BMW PPF San Diego | Paint Protection Film for BMW"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'BMW paint protection film in San Diego from Mr Wrap in Vista. Clear, matte, stealth, and colored PPF starting at $499. 13 years of professional installation experience. Call or text 858-282-8119.'
      )
    }

    return () => {
      faqScript.remove()
      serviceScript.remove()
      breadcrumbScript.remove()
    }
  }, [])

  return (
    <>
      <CanonicalTag url="/bmw-ppf-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
        <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-cyan-400 font-semibold mb-3">Mr Wrap · Vista, CA</p>
                <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">BMW Paint Protection Film in San Diego</h1>
                <p className="text-lg sm:text-xl text-gray-300 mb-4">
                  Protect BMW paint from freeway chips, coastal sun, and daily wear with professional PPF installation at our Vista shop. We serve BMW owners throughout North County and San Diego.
                </p>
                <p className="text-lg text-gray-300 mb-8">
                  13 years of professional installation experience. Tell us your BMW model and coverage goals for exact pricing.
                </p>
                <div className="flex gap-3 justify-start flex-wrap">
                  <button
                    id="bmw-ppf-hero-quote"
                    onClick={goToQuote}
                    className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition"
                  >
                    <Calendar size={18} />
                    Get Exact Pricing for Your BMW
                  </button>
                  <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                    <Phone size={18} />
                    Call
                  </a>
                  <a href="sms:858-282-8119" className="flex items-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 border-2 border-cyan-500 text-cyan-400 px-6 py-3 rounded-lg font-semibold transition">
                    <MessageCircle size={18} />
                    Text
                  </a>
                </div>
              </div>
              <div>
                <EmbeddedQuoteForm
                  defaultService="ppf"
                  heading="Get Exact BMW PPF Pricing"
                  subheading="Include your BMW year and model. We will follow up with coverage options and a clear quote from the Vista shop."
                />
              </div>
            </div>
          </div>
        </section>

        <nav aria-label="Breadcrumb" className="bg-black border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <ol className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              <li><a href="/" className="hover:text-cyan-400 transition">Home</a></li>
              <li className="text-gray-600">›</li>
              <li><a href="/paint-protection-film-san-diego" className="hover:text-cyan-400 transition">Paint Protection Film San Diego</a></li>
              <li className="text-gray-600">›</li>
              <li className="text-cyan-400 font-medium" aria-current="page">BMW PPF San Diego</li>
            </ol>
          </div>
        </nav>

        <section className="bg-gray-950 border-b border-cyan-500/20 py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <Award className="text-cyan-400 flex-shrink-0" size={22} />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">13 Years Experience</p>
                  <p className="text-gray-400 text-xs">Professional installation</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="text-cyan-400 flex-shrink-0" size={22} />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Vista Shop</p>
                  <p className="text-gray-400 text-xs">North County & San Diego</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="text-cyan-400 flex-shrink-0" size={22} />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">BMW PPF + Tint</p>
                  <p className="text-gray-400 text-xs">Same-shop complementary work</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Car className="text-cyan-400 flex-shrink-0" size={22} />
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Sedans, SUVs & M Cars</p>
                  <p className="text-gray-400 text-xs">Quoted by model and coverage</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">BMW PPF for San Diego Driving</h2>
            <div className="space-y-5 text-gray-300 text-lg">
              <p>
                BMW paint is designed to look sharp up close. That same finish shows rock chips quickly on San Diego freeways and UV fade over time near the coast. <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">Paint protection film</a> adds a clear, self-healing layer over the panels that take the most abuse.
              </p>
              <p>
                Mr Wrap installs BMW PPF in Vista. Owners typically come from North County and greater San Diego when they want the work done indoors, with the film and coverage explained before anything is applied.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white text-center">BMW PPF Pricing in San Diego</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto text-center mb-12">
              These are the same starting prices used across our <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">PPF pricing</a>. Exact BMW pricing depends on model, coverage, film, body complexity, and installation requirements. Larger SUVs and complex bodywork may cost more.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {[
                { name: "Front Bumper", price: "Starting at $499+", note: "Highest-impact entry coverage" },
                { name: "Partial Front", price: "Starting at $1,299+", note: "Bumper, partial hood, mirrors, headlights" },
                { name: "Full Front", price: "Starting at $1,899+", note: "Most requested new-BMW package" },
                { name: "Full Vehicle Gloss", price: "Starting at $4,499+", note: "Complete painted-surface protection" },
                { name: "Matte / Stealth", price: "Starting at $5,499+", note: "For matte or Frozen BMW finishes" },
                { name: "Colored PPF", price: "Starting at $5,999+", note: "Color change plus protection" }
              ].map((tier) => (
                <div key={tier.name} className="bg-black p-6 rounded-xl border border-cyan-500/20">
                  <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
                  <div className="text-cyan-400 text-2xl font-bold mb-2">{tier.price}</div>
                  <p className="text-gray-400 text-sm">{tier.note}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-400 max-w-3xl mx-auto mb-8">
              Starting prices are not locked quotes. A 3 Series, X5, and M car are priced individually after we know the coverage and film.
            </p>
            <div className="text-center">
              <button onClick={goToQuote} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition inline-flex items-center gap-2">
                <Calendar size={20} />
                Get Exact Pricing for Your BMW
              </button>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white text-center">BMW Coverage We Install Most Often</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto text-center mb-12">
              Coverage is built around how a BMW is actually driven, not a generic diagram.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-3">Front bumper</h3>
                <p className="text-gray-300">The first panel to chip on freeway commutes. A practical starting point if you want protection without a full front kit.</p>
              </div>
              <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-3">Hood, fenders & mirrors</h3>
                <p className="text-gray-300">Partial-front and full-front packages cover the large painted surfaces that collect debris and wash swirls.</p>
              </div>
              <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-3">Headlights where appropriate</h3>
                <p className="text-gray-300">Headlight film is added when the lens shape and owner goals call for it. Not every BMW needs the same lighting coverage.</p>
              </div>
              <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-3">Rocker panels & high-impact areas</h3>
                <p className="text-gray-300">Lower doors and rockers see sand, gravel, and parking wear. These areas are often added to front packages on SUVs and daily drivers.</p>
              </div>
              <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-3">Full front</h3>
                <p className="text-gray-300">The usual recommendation for a new BMW: bumper, full hood, fenders, mirrors, and headlights as needed.</p>
              </div>
              <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                <h3 className="text-xl font-bold text-white mb-3">Full vehicle</h3>
                <p className="text-gray-300">Gloss, matte/stealth, or <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">colored PPF</a> when you want every painted panel covered or a finish change with protection.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-white">How We Install BMW PPF</h2>
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-white">Professional preparation</h3>
                  <p className="text-gray-300">The BMW is washed, decontaminated, and inspected before film goes on. Existing swirls or light defects are addressed when they would show under the film.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-white">Method selected by vehicle and panel</h3>
                  <p className="text-gray-300">We use bulk film or precision-cut patterns based on the BMW and the panel. Some areas cut cleaner from a pattern; others need hand work. We do not use only one method on every car.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-white">Wrapped edges and disassembly where appropriate</h3>
                  <p className="text-gray-300">Edges are wrapped or tucked where it improves the finish. Trim, badges, lights, or bumper pieces are removed when that is the cleaner path. Not every edge is wrapped and not every panel is disassembled.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-white">Clean professional finish</h3>
                  <p className="text-gray-300">The install is checked for adhesion, alignment, and contamination before the BMW leaves. You get care guidance and the manufacturer warranty terms that apply to the film selected.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">BMW PPF and Ceramic Window Tint</h2>
            <div className="space-y-5 text-gray-300 text-lg">
              <p>
                A lot of BMW owners want paint protection and cabin comfort at the same time. We can install PPF and <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">premium ceramic window tint</a> at the same Vista shop, so you are not coordinating two appointments across town.
              </p>
              <p>
                Tint stays optional. If you only want film on the paint, that is the quote we build. If you want both, we plan coverage, legal shade, and timing around the BMW rather than selling an extra package.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white text-center">Real BMW PPF Projects by Mr Wrap</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto text-center mb-12">
              Recent BMW paint protection film work from the Vista shop, including matte/stealth-style coverage, <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">colored PPF</a>, and PPF paired with <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">window tint</a>.
            </p>

            <article className="mb-14">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <h3 className="text-2xl sm:text-3xl font-bold text-white">BMW SUV — Full Matte Black PPF</h3>
                <span className="text-xs uppercase tracking-wide text-cyan-400 border border-cyan-500/40 px-2.5 py-1 rounded-full">Matte / Stealth-style</span>
              </div>
              <p className="text-gray-300 mb-6 max-w-4xl">
                Full matte black PPF and window tint on a BMW SUV. The finish was photographed after installation so the film texture, panel lines, and tinted glass are visible in both outdoor and shop lighting.
              </p>
              <div className="grid md:grid-cols-5 gap-4">
                <figure className="md:col-span-3 bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                  <img
                    src="https://static.lumi.new/2c/2c60956f046876e56c6c1b5547da2dc1.webp"
                    alt="Finished BMW SUV with full matte black PPF and window tint"
                    width={1600}
                    height={1200}
                    className="w-full h-64 sm:h-80 lg:h-[26rem] object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="p-4 text-gray-400 text-sm">Finished BMW SUV after full matte black PPF and window tint</figcaption>
                </figure>
                <div className="md:col-span-2 grid gap-4">
                  <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                    <img
                      src="https://static.lumi.new/94/9483e45e55597663002facb04f085dca.webp"
                      alt="Close-up of matte black PPF around a BMW SUV headlight and hood edge"
                      width={1200}
                      height={900}
                      className="w-full h-40 sm:h-44 object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="p-3 text-gray-400 text-sm">Headlight and hood-edge finish after PPF</figcaption>
                  </figure>
                  <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                    <img
                      src="https://static.lumi.new/93/939d233e92769468e0bc22ff9bc4c3df.webp"
                      alt="BMW SUV in the Vista shop during matte black PPF installation"
                      width={1200}
                      height={900}
                      className="w-full h-40 sm:h-44 object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="p-3 text-gray-400 text-sm">In-shop view during the SUV install</figcaption>
                  </figure>
                </div>
              </div>
            </article>

            <article>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <h3 className="text-2xl sm:text-3xl font-bold text-white">2026 BMW — Full Colored PPF</h3>
                <span className="text-xs uppercase tracking-wide text-cyan-400 border border-cyan-500/40 px-2.5 py-1 rounded-full">Colored PPF + Tint</span>
              </div>
              <p className="text-gray-300 mb-6 max-w-4xl">
                Full <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">colored PPF</a> on a 2026 BMW, completed with full <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">window tint</a> including the windshield. The photos show the finished color change, the tinted glass, and film being worked on the hood in the shop.
              </p>
              <div className="grid md:grid-cols-5 gap-4">
                <figure className="md:col-span-3 bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                  <img
                    src="https://static.lumi.new/a6/a6b573f03714de74d5c64f3d933167a1.webp"
                    alt="Finished 2026 BMW with full colored PPF and full window tint including the windshield"
                    width={1600}
                    height={1200}
                    className="w-full h-64 sm:h-80 lg:h-[26rem] object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="p-4 text-gray-400 text-sm">Finished 2026 BMW after full colored PPF and windshield-inclusive tint</figcaption>
                </figure>
                <div className="md:col-span-2 grid gap-4">
                  <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                    <img
                      src="https://static.lumi.new/4b/4b697d66424b554e2bf2f33bbf57ac23.webp"
                      alt="Installer applying colored PPF to the hood of a 2026 BMW"
                      width={1200}
                      height={900}
                      className="w-full h-40 sm:h-44 object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="p-3 text-gray-400 text-sm">Hood install in progress at the Vista shop</figcaption>
                  </figure>
                  <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                    <img
                      src="https://static.lumi.new/09/0952bc5651004c23e42791c381adc58b.webp"
                      alt="Colored PPF tucked around a BMW door handle after installation"
                      width={1200}
                      height={900}
                      className="w-full h-40 sm:h-44 object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="p-3 text-gray-400 text-sm">Door-handle finish after colored PPF</figcaption>
                  </figure>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section className="py-16 bg-gray-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white text-center">BMW Work From the Vista Shop</h2>
            <p className="text-gray-300 text-center max-w-3xl mx-auto mb-12">
              These are existing Mr Wrap BMW photos and a real BMW PPF + ceramic tint review already used on the site. No staged or invented BMW projects.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                <img
                  src="https://static.lumi.new/13/13629e80aa9870537fe8adc495e1a951.webp"
                  alt="BMW X5 matte gray PPF installation in the Mr Wrap Vista shop"
                  className="w-full h-64 object-cover"
                />
                <figcaption className="p-4 text-gray-300 text-sm">BMW X5 — matte gray PPF installation in shop</figcaption>
              </figure>
              <figure className="bg-black rounded-xl border border-cyan-500/20 overflow-hidden">
                <img
                  src="https://static.lumi.new/5b/5bde8a60bc95c8d032447e94cfa13ddc.webp"
                  alt="Satin black PPF installation on a BMW at Mr Wrap"
                  className="w-full h-64 object-cover"
                />
                <figcaption className="p-4 text-gray-300 text-sm">Satin black PPF install — BMW project from the shop gallery</figcaption>
              </figure>
            </div>
            <blockquote className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="flex items-start gap-4 mb-4">
                <img
                  src="https://static.lumi.new/04/04d2303152a94c2d0426a20f8de35776.webp"
                  alt="BMW X5 M50i after PPF and full ceramic tint at Mr Wrap"
                  className="w-16 h-16 rounded-full object-cover border-2 border-cyan-500 flex-shrink-0"
                />
                <div>
                  <p className="font-bold text-white">BMW X5 M50i Owner</p>
                  <p className="text-sm text-gray-400">BMW X5 M50i - PPF & Full Ceramic Tint</p>
                </div>
              </div>
              <p className="text-gray-300 italic leading-relaxed">
                "The Mr. Wrap team did an impeccable job installing a PPF and Full Ceramic tint on my BMW X5 M50i. I was guaranteed the lowest price in San Diego and they did not disappoint in price and quality. The results were astounding in comparison to other quotes and shops I visited. From turn-in to retrieval they were detail oriented and extremely knowledgeable about their preparation materials, installation quality and aftercare for long lasting results. They did not lead me astray. Not only did they remain flexible and attentive to my requests, they continued to entertain any thoughts or concerns that arose throughout the entire process. Highly recommend the Mr. Wrap team for anyone looking for affordable quality expertise and years of experience wrapped (no pun intended) into one location!"
              </p>
            </blockquote>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-white text-center">BMW PPF Questions We Hear Most</h2>
            <div className="space-y-6">
              {faqs.map((faq) => (
                <div key={faq.question} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                  <h3 className="text-xl font-bold mb-3 text-white">{faq.question}</h3>
                  <p className="text-gray-300">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <EmbeddedQuoteForm
              defaultService="ppf"
              heading="Request a BMW PPF Quote"
              subheading="Share the BMW model, mileage or lease status, and whether you also want ceramic tint. We reply from the Vista shop."
            />
          </div>
        </section>

        <section className="py-16 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <a href="/paint-protection-film-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
                <p className="text-gray-400 text-sm">Full PPF coverage options and starting prices for every make</p>
              </a>
              <a href="/window-tint-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Window Tint San Diego</h3>
                <p className="text-gray-400 text-sm">Ceramic tint that pairs cleanly with BMW PPF</p>
              </a>
              <a href="/colored-ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Colored PPF San Diego</h3>
                <p className="text-gray-400 text-sm">Color-change film when you want protection and a new finish</p>
              </a>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4 text-white flex items-center justify-center gap-3">
              <MapPin className="text-cyan-400" size={28} />
              Installed in Vista for North County and San Diego BMW Owners
            </h2>
            <p className="text-gray-300 text-lg mb-4">
              The shop is at 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084. BMW owners typically come from North County and across San Diego County for indoor PPF installation.
            </p>
            <p className="text-gray-400">
              <Clock className="inline mr-2 mb-1" size={16} />
              Monday–Friday 8:00 AM–6:00 PM · Saturday 9:00 AM–4:00 PM
            </p>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-r from-cyan-500 to-cyan-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">Get Exact Pricing for Your BMW</h2>
            <p className="text-xl text-white/90 mb-8">
              Call, text, or send the quote form. We will price the BMW by model and coverage — no generic guess.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={goToQuote} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Calendar size={20} />
                Get Exact Pricing for Your BMW
              </button>
              <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Phone size={20} />
                Call 858-282-8119
              </a>
              <a href="sms:858-282-8119" className="bg-black/20 hover:bg-black/30 text-white font-bold py-4 px-10 rounded-lg transition inline-flex items-center justify-center gap-2 border border-white/40">
                <MessageCircle size={20} />
                Text Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default BmwPpfSanDiegoPage
